import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrency, formatCurrencyLabel } from '../../utils/formatUtils';
import '../styles/admin-theme.css';

const formatDate = (d) => {
  if (d == null || d === '') return '';
  try {
    if (typeof d === 'string') {
      const s = d.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      if (d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      const date = new Date(d);
      if (Number.isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    if (typeof d.getTime === 'function') {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    return '';
  } catch {
    return '';
  }
};

const emptyLine = () => ({
  company_license_id: '',
  quantity: 1,
  unit_price: '',
  discount_percent: '',
  prorata_days: '',
  amount: '',
});

function salesPriceFrom(unitPrice, discountPercent) {
  const u = Number(unitPrice);
  const d = Number(discountPercent);
  if (u === 0 || isNaN(u)) return 0;
  return u * (1 - (isNaN(d) ? 0 : d) / 100);
}

/** Total days in billing period (subscription start to end inclusive). */
function getTotalDaysInCycle(sub) {
  if (!sub?.start_date || !sub?.end_date) return 0;
  const s = new Date(String(sub.start_date).slice(0, 10) + 'T00:00:00');
  const e = new Date(String(sub.end_date).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const diffMs = e - s;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays + 1);
}

/** Prorata days from license start_date to end_date (inclusive). */
function prorataDaysFromLicense(license) {
  const start = license?.start_date;
  const end = license?.end_date;
  if (!start || !end) return '';
  const s = new Date(String(start).slice(0, 10) + 'T00:00:00');
  const e = new Date(String(end).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const diffMs = e - s;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays + 1);
}

/** Prorata amount = (Full Period Price / Total Days in Billing Cycle) × Prorata Days × Quantity. */
function prorataAmount(salesPrice, totalDaysInCycle, prorataDays, quantity) {
  const qty = Number(quantity) || 0;
  if (totalDaysInCycle <= 0 || prorataDays == null || prorataDays === '' || Number(prorataDays) <= 0) {
    return qty * (salesPrice || 0);
  }
  return ((salesPrice || 0) / totalDaysInCycle) * Number(prorataDays) * qty;
}

const CreateInvoiceModal = ({ show, onHide, companyId, subscriptions, onSuccess }) => {
  const submitGuardRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pricebookEntries, setPricebookEntries] = useState([]);
  const [form, setForm] = useState({
    subscription_id: '',
    invoice_date: formatDate(new Date()),
    status: 'DRAFT',
  });
  const [lineItems, setLineItems] = useState([emptyLine()]);

  const activeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

  useEffect(() => {
    if (show && activeSubscriptions.length > 0 && !form.subscription_id) {
      setForm((prev) => ({ ...prev, subscription_id: activeSubscriptions[0].id }));
    }
  }, [show, activeSubscriptions, form.subscription_id]);

  const selectedSub = activeSubscriptions.find((s) => s.id === form.subscription_id);

  useEffect(() => {
    if (!selectedSub?.pricebook_id) {
      setPricebookEntries([]);
      return;
    }
    const load = async () => {
      try {
        const list = await adminSchemaApi.fetchPricebookEntries(selectedSub.pricebook_id);
        setPricebookEntries(Array.isArray(list) ? list : []);
      } catch {
        setPricebookEntries([]);
      }
    };
    load();
  }, [selectedSub?.pricebook_id]);

  useEffect(() => {
    if (!form.subscription_id || !activeSubscriptions.length) return;
    const sub = activeSubscriptions.find((s) => s.id === form.subscription_id);
    const notInvoiced = (sub && sub.licenses_not_invoiced) ? sub.licenses_not_invoiced : [];
    if (notInvoiced.length > 0) {
      setLineItems(
        notInvoiced.map((lic) => ({
          company_license_id: lic.id,
          quantity: lic.quantity ?? 1,
          unit_price: '',
          discount_percent: '',
          prorata_days: prorataDaysFromLicense(lic),
          amount: '',
        }))
      );
    } else {
      setLineItems([emptyLine()]);
    }
  }, [form.subscription_id]);

  const totalDaysInCycle = selectedSub ? getTotalDaysInCycle(selectedSub) : 0;
  const pricebookCurrency = selectedSub?.pricebook_currency || 'INR';

  useEffect(() => {
    if (pricebookEntries.length === 0 || lineItems.length === 0) return;
    const planId = selectedSub?.plan_id;
    setLineItems((prev) =>
      prev.map((line) => {
        if (!line.company_license_id) return line;
        const lic = activeSubscriptions
          .flatMap((s) => s.licenses || [])
          .find((l) => l.id === line.company_license_id);
        if (!lic || !lic.license_type_id) return line;
        const entry = pricebookEntries.find(
          (e) => e.productid === lic.license_type_id && e.plan_id === planId
        );
        if (!entry) return line;
        const up = entry.list_price != null ? Number(entry.list_price) : '';
        const disc = entry.discount_percent != null ? Number(entry.discount_percent) : '';
        const sp = salesPriceFrom(up, disc);
        const qty = Number(line.quantity) || 0;
        const prorata = prorataDaysFromLicense(lic);
        const prorataDaysVal = prorata !== '' ? prorata : line.prorata_days;
        const amt = prorataAmount(sp, totalDaysInCycle, prorataDaysVal, qty);
        return {
          ...line,
          unit_price: up,
          discount_percent: disc,
          prorata_days: prorataDaysVal !== '' ? prorataDaysVal : line.prorata_days,
          amount: amt.toFixed(2),
        };
      })
    );
  }, [selectedSub?.pricebook_id, pricebookEntries.length, form.subscription_id, totalDaysInCycle, selectedSub?.plan_id]);

  const availableLicenses = (selectedSub && selectedSub.licenses) ? selectedSub.licenses : [];
  const licensesNotInvoiced = (selectedSub && selectedSub.licenses_not_invoiced) ? selectedSub.licenses_not_invoiced : [];

  const handleClose = () => {
    submitGuardRef.current = false;
    setForm({ subscription_id: '', invoice_date: formatDate(new Date()), status: 'DRAFT' });
    setLineItems([emptyLine()]);
    setError('');
    onHide();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'subscription_id') {
      const sub = activeSubscriptions.find((s) => s.id === value);
      const notInvoiced = (sub && sub.licenses_not_invoiced) ? sub.licenses_not_invoiced : [];
      setLineItems(
        notInvoiced.length > 0
          ? notInvoiced.map((lic) => ({
              company_license_id: lic.id,
              quantity: lic.quantity ?? 1,
              unit_price: '',
              discount_percent: '',
              prorata_days: prorataDaysFromLicense(lic),
              amount: '',
            }))
          : [emptyLine()]
      );
    }
  };

  const addLine = () => {
    setLineItems((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (index) => {
    setLineItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const updateLine = (index, field, value) => {
    setLineItems((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value };
        const qty = field === 'quantity' ? (value === '' ? 0 : Number(value)) : (Number(row.quantity) || 0);
        const up = field === 'unit_price' ? (value === '' ? 0 : Number(value)) : (Number(row.unit_price) || 0);
        const disc = field === 'discount_percent' ? (value === '' ? 0 : Number(value)) : (Number(row.discount_percent) || 0);
        const prorataDays = field === 'prorata_days' ? (value === '' ? null : value) : (row.prorata_days !== '' ? row.prorata_days : null);
        const sp = salesPriceFrom(up, disc);
        next.amount = prorataAmount(sp, totalDaysInCycle, prorataDays, qty).toFixed(2);
        return next;
      })
    );
  };

  const setLineFromLicense = (index, licenseId) => {
    const lic = availableLicenses.find((l) => l.id === licenseId);
    if (!lic) return;
    const entry = lic.license_type_id && pricebookEntries.find(
      (e) => e.productid === lic.license_type_id && e.plan_id === selectedSub?.plan_id
    );
    const up = entry && entry.list_price != null ? Number(entry.list_price) : '';
    const disc = entry && entry.discount_percent != null ? Number(entry.discount_percent) : '';
    const qty = lic.quantity || 1;
    const sp = salesPriceFrom(up, disc);
    const prorata = prorataDaysFromLicense(lic);
    const prorataDaysVal = prorata !== '' ? prorata : '';
    const amt = prorataAmount(sp, totalDaysInCycle, prorataDaysVal, qty);
    setLineItems((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              company_license_id: lic.id,
              quantity: qty,
              unit_price: up,
              discount_percent: disc,
              prorata_days: prorataDaysVal !== '' ? prorataDaysVal : row.prorata_days || '',
              amount: amt.toFixed(2),
            }
          : row
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitGuardRef.current) return;
    if (!companyId || !form.subscription_id) {
      setError('Please select a subscription.');
      return;
    }
    if (!selectedSub?.pricebook_id) {
      setError('Subscription has no pricebook. Edit the subscription to set a pricebook first.');
      return;
    }
    if (!form.invoice_date) {
      setError('Invoice date is required.');
      return;
    }
    const planId = selectedSub?.plan_id;
    const licensesWithoutPrice = selectedSub?.pricebook_id ? lineItems
      .filter((l) => l.company_license_id)
      .map((line) => {
        const lic = activeSubscriptions
          .flatMap((s) => s.licenses || [])
          .find((l2) => l2.id === line.company_license_id);
        return lic ? { lic, line } : null;
      })
      .filter(Boolean)
      .filter(({ lic }) => {
        const entry = pricebookEntries.find(
          (e) => e.productid === lic.license_type_id && e.plan_id === planId
        );
        return !entry;
      }) : [];
    if (licensesWithoutPrice.length > 0) {
      const names = licensesWithoutPrice
        .map(({ lic }) => lic.license_type_name || lic.license_type_code || 'Unknown')
        .join(', ');
      setError(
        `Cannot create invoice: no pricebook entry found for license type(s): ${names}. Add prices for these licenses in the plan's Detail page or in the pricebook.`
      );
      return;
    }
    const validLines = lineItems
      .map((l) => {
        const qty = l.quantity != null && l.quantity !== '' ? Number(l.quantity) : null;
        const up = l.unit_price != null && l.unit_price !== '' ? Number(l.unit_price) : null;
        const disc = l.discount_percent != null && l.discount_percent !== '' ? Number(l.discount_percent) : 0;
        const salesPrice = (up || 0) * (1 - disc / 100);
        const prorataDaysVal = l.prorata_days != null && l.prorata_days !== '' ? Number(l.prorata_days) : null;
        const amt = l.amount != null && l.amount !== '' ? Number(l.amount) : (qty ? prorataAmount(salesPrice, totalDaysInCycle, prorataDaysVal, qty) : null);
        if (amt == null && !l.company_license_id) return null;
        return {
          company_license_id: l.company_license_id || undefined,
          quantity: qty,
          unit_price: up ?? undefined,
          discount_percent: l.discount_percent !== '' ? Number(l.discount_percent) : undefined,
          prorata_days: l.prorata_days !== '' ? Number(l.prorata_days) : undefined,
          amount: amt ?? (qty ? prorataAmount(salesPrice, totalDaysInCycle, prorataDaysVal, qty) : 0),
        };
      })
      .filter(Boolean);
    if (validLines.length === 0) {
      setError('Add at least one line item (license or amount).');
      return;
    }
    submitGuardRef.current = true;
    setSaving(true);
    setError('');
    try {
      await adminSchemaApi.createInvoice(companyId, form.subscription_id, {
        invoice_date: form.invoice_date,
        status: form.status || undefined,
        line_items: validLines,
      });
      handleClose();
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create invoice.');
      submitGuardRef.current = false;
    } finally {
      setSaving(false);
      submitGuardRef.current = false;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">Create Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {activeSubscriptions.length === 0 ? (
          <Alert variant="info">Create a subscription first before creating invoices.</Alert>
        ) : (
          <>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Form onSubmit={handleSubmit}>
              <div className="mb-3">
                <Form.Label>Subscription</Form.Label>
                <Form.Select
                  name="subscription_id"
                  value={form.subscription_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select subscription</option>
                  {activeSubscriptions.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.billing_cycle} · {formatDate(sub.start_date)} – {formatDate(sub.end_date)}
                      {sub.pricebook_name ? ` (${sub.pricebook_name})` : ''}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">Invoice uses the subscription&apos;s pricebook. Select a subscription that has a pricebook set.</Form.Text>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Label>Invoice date</Form.Label>
                  <Form.Control
                    type="date"
                    name="invoice_date"
                    value={form.invoice_date}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label>Status</Form.Label>
                  <Form.Select name="status" value={form.status} onChange={handleFormChange}>
                    <option value="DRAFT">Draft</option>
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                    <option value="CANCELLED">Cancelled</option>
                  </Form.Select>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Line items</Form.Label>
                <Button type="button" variant="outline-primary" size="sm" onClick={addLine}>
                  + Add line
                </Button>
              </div>
              {licensesNotInvoiced.length > 0 ? (
                <p className="admin-subtitle small mb-2 text-success">
                  {licensesNotInvoiced.length} license(s) not yet invoiced — line items below are pre-filled. Add unit price/amount or leave as 0.
                </p>
              ) : (
                <p className="admin-subtitle small mb-2">
                  All licenses under this subscription are already invoiced. Add manual line items or link to a license below.
                </p>
              )}
              <Table size="sm" bordered className="mb-3">
                <thead>
                  <tr>
                    <th>License (optional)</th>
                    <th>Qty</th>
                    <th>Unit price ({formatCurrencyLabel(pricebookCurrency)})</th>
                    <th>Discount %</th>
                    <th>Sales price</th>
                    <th>Prorata days</th>
                    <th>Amount</th>
                    <th className="text-center" style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((line, index) => {
                    const sp = salesPriceFrom(line.unit_price, line.discount_percent);
                    return (
                    <tr key={index}>
                      <td>
                        <Form.Select
                          size="sm"
                          value={line.company_license_id}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateLine(index, 'company_license_id', v);
                            if (v) setLineFromLicense(index, v);
                          }}
                        >
                          <option value="">— Manual —</option>
                          {availableLicenses.map((lic) => (
                            <option key={lic.id} value={lic.id}>
                              {lic.license_type_name || lic.license_type_code} (×{lic.quantity})
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          value={line.quantity}
                          onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          min={0}
                          value={line.unit_price}
                          onChange={(e) => updateLine(index, 'unit_price', e.target.value)}
                          placeholder="From pricebook"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          value={line.discount_percent}
                          onChange={(e) => updateLine(index, 'discount_percent', e.target.value)}
                          placeholder="From pricebook"
                        />
                      </td>
                      <td className="align-middle">{sp > 0 ? formatCurrency(sp, pricebookCurrency) : '—'}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          value={line.prorata_days}
                          onChange={(e) => updateLine(index, 'prorata_days', e.target.value)}
                          placeholder="From license period"
                        />
                      </td>
                      <td className="align-middle">{line.amount != null && line.amount !== '' ? formatCurrency(line.amount, pricebookCurrency) : '—'}</td>
                      <td className="text-center">
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          className="px-2"
                          onClick={() => removeLine(index)}
                          disabled={lineItems.length === 1}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </Table>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" className="admin-primary-btn" disabled={saving}>
                  {saving ? 'Creating…' : 'Create invoice'}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CreateInvoiceModal;
